import React, { useRef } from 'react';
import { Label, Loading, Modal } from '@brighterion/br-ui';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormikInputField, FormikFieldTextArea } from 'Components/FormikComponents';
import {
    GeneralListEntry,
    ListEntryApiResponseObj,
    ListEntryFilterResponseObj,
    PanListEntry,
    RangeListEntry
} from '@brighterion/types/listEntry';
import usePatchListEntry from 'Hooks/query-hooks/listEntry/usePatchListEntry';
import { getEditListEntryFormInitialValues } from '@brighterion/utils/listEntry';
import { getListEntryFormSchema } from 'Routes/ListManagement/schemas';
import BcatsMaskedField from 'Components/Table/Cell/BcatsMaskedField';
import { LIST_TYPES } from 'Common/enums';
import DatePickerWrapper from '../DatePickerWrapper/DatePickerWrapper';
import EndDateTimePicker from '../EndDateTimePicker';
import styles from './EditListEntryModal.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    merchantSpecific: boolean;
    listEntries: ListEntryFilterResponseObj['result'];
    listEntryId: number;
    listId: number;
    listType: SingleListType;
    customListType?: string;
};

const EditListEntryModal = ({
    isOpen,
    onClose,
    merchantSpecific,
    listEntries,
    listEntryId,
    listId,
    listType,
    customListType
}: Props) => {
    const { t } = useTranslation();
    const formRef = useRef<FormikProps<GeneralListEntry | PanListEntry | RangeListEntry | ListEntryApiResponseObj>>(
        null
    );
    const mutation = usePatchListEntry({ listId, onClose, formRef });
    const currentListEntry = listEntries.find((entry) => entry.id === listEntryId);
    const submitForm = (formValues: GeneralListEntry | PanListEntry | RangeListEntry | ListEntryApiResponseObj) => {
        const { startTime, endTime, comment } = formValues;

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Lists - Edit List Entry',
                text: 'Edit List Entry'
            }
        });

        mutation.mutate({
            listEntryId,
            startTime,
            endTime,
            comment
        });
    };

    if (!currentListEntry) {
        return <div>list entry does not exist</div>;
    }
    const formInitialValues = getEditListEntryFormInitialValues({ listType, currentListEntry });
    const isInitialStartTimePast = new Date(formInitialValues.startTime).getTime() <= new Date().getTime();

    return (
        <Modal
            dataTestId='edit-list-entry-modal'
            isOpen={isOpen}
            closeOnClickOutside={false}
            closeModal={onClose}
            containerClass={styles.modalContainer}
        >
            <Modal.Title
                id='edit-list-entry-modal-title'
                title='Edit Entry'
                onClose={onClose}
                isSubmitting={mutation.isLoading}
            />

            <Formik
                initialValues={getEditListEntryFormInitialValues({ listType, currentListEntry })}
                validationSchema={getListEntryFormSchema({
                    customListType,
                    listType,
                    t,
                    schemaToOmit: isInitialStartTimePast ? ['startTime'] : []
                })}
                onSubmit={submitForm}
                innerRef={formRef}
            >
                {(formikProps) => {
                    const disableSave = !formikProps.dirty || !formikProps.isValid;

                    return (
                        <div>
                            {merchantSpecific && (
                                <div className={styles.merchantSpecific}>
                                    <Label label={t('Merchant')} id='merchantId' />
                                    <div className={styles.fieldsContainer}>
                                        <FormikInputField
                                            type='string'
                                            name='merchantOption'
                                            overrideValue={
                                                (formikProps.values as GeneralListEntry | PanListEntry | RangeListEntry)
                                                    .merchantOption[0]
                                            }
                                            dataTestId='edit-list-entry-modal-merchant-option-field'
                                            handleChange={formikProps.handleChange}
                                            handleBlur={formikProps.handleBlur}
                                            disabled
                                        />
                                        <FormikInputField
                                            type='string'
                                            name='merchantId'
                                            placeholder='--'
                                            dataTestId='edit-list-entry-modal-merchant-id-field'
                                            handleChange={formikProps.handleChange}
                                            handleBlur={formikProps.handleBlur}
                                            disabled
                                        />
                                    </div>
                                </div>
                            )}
                            {listType === LIST_TYPES.PAN ? (
                                // since exact match is required to search a PAN, there will be only 1 entry per search
                                // To avoid additional state to get the row id of the list entry in the list entry table
                                // just pass in 0 for rowId
                                <div className={styles.panValueInputFieldContainer}>
                                    <Label
                                        label={t('Value')}
                                        id='panValueInputFieldLabel'
                                        className={styles.panValueInputFieldLabel}
                                    />
                                    <div className={styles.panValueInputFieldStyle}>
                                        <BcatsMaskedField value={formikProps.values.value} rowId='0' />
                                    </div>
                                </div>
                            ) : (
                                <FormikInputField
                                    type='string'
                                    name='value'
                                    label='Value'
                                    dataTestId='edit-list-entry-modal-value-field'
                                    handleBlur={formikProps.handleBlur}
                                    handleChange={formikProps.handleChange}
                                    disabled
                                    className={styles.valueField}
                                />
                            )}
                            <div id='select-startTime-option' className={styles.radioButtonGroupLabel}>
                                {t('Start Date and Time')}
                            </div>

                            <Field name='startTime'>
                                {({ field, meta }: FieldProps) => {
                                    return (
                                        <div
                                            className={
                                                isInitialStartTimePast
                                                    ? styles.disabledDatePickerStyle
                                                    : styles.startTimeContainer
                                            }
                                        >
                                            <DatePickerWrapper
                                                selected={
                                                    !formInitialValues.startTime
                                                        ? new Date(currentListEntry.createdAt)
                                                        : new Date(field.value)
                                                }
                                                onChange={(date: any) => {
                                                    formikProps.setFieldValue(field.name, date);
                                                }}
                                                disabled={formikProps.isSubmitting || isInitialStartTimePast}
                                                showIcon={!isInitialStartTimePast}
                                            />
                                            {meta.error && <div className={styles.labelError}>{meta.error}</div>}
                                        </div>
                                    );
                                }}
                            </Field>
                            {isInitialStartTimePast && (
                                <div className={styles.helperTxt}>
                                    {t('Start date cannot be modified since it is in the past')}
                                </div>
                            )}

                            <EndDateTimePicker formikProps={formikProps} />
                            <FormikFieldTextArea
                                label='Comment (Optional)'
                                name='comment'
                                dataTestId='edit-list-entry-modal-comment-field'
                                value={formikProps.values?.comment ?? ''}
                                lineNum={4}
                                showCounter={false}
                                onChange={formikProps.handleChange}
                                onBlur={formikProps.handleBlur}
                                className={styles.comment}
                                disabled={mutation.isLoading || formikProps.isSubmitting}
                            />
                            <Modal.Footer
                                id='edit-list-entry-modal-footer'
                                onActionText={
                                    mutation.isLoading ? (
                                        <div data-testid='edit-loader'>
                                            <Loading size='sm' fillName='white' />
                                        </div>
                                    ) : (
                                        t('Save')
                                    )
                                }
                                onAction={formikProps.handleSubmit}
                                onCloseText={t('Cancel')}
                                onClose={onClose}
                                disableOnAction={disableSave || formikProps.isSubmitting || mutation.isLoading}
                                disableOnCancel={formikProps.isSubmitting || mutation.isLoading}
                            />
                        </div>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default EditListEntryModal;
